import type { AiTurn } from '../connect/contract.ts';
import type { AiProvider } from '../connect/provider.ts';
import type { AnswerMessage, ChatMessage, ConversationSummary } from './types.ts';

export const CHAT_RECOVERY_ACTION = 'recover-live-run';
export function chatUsageUnknown(messages: readonly ChatMessage[]): boolean {
  return messages.some(
    (message) =>
      message.role === 'assistant' &&
      message.kind === 'answer' &&
      /نامشخص|انتظار/.test(message.footer?.usageLabel ?? ''),
  );
}
export interface ChatRunRecovery {
  readonly conversationId: string;
  readonly cardId: string;
  readonly quoteId: string;
  readonly idempotencyKey: string;
}
export interface ChatRecoveryStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Local UI identities never pretend to be server-created conversation UUIDs. */
export function newChatIdentity(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

export function chatStorageKey(
  base: string,
  principalId: string | null | undefined,
): string | null {
  return principalId ? `sakhtar:ai:chat:v2:${base}:${principalId}` : null;
}

const opaque = (value: unknown): value is string =>
  typeof value === 'string' && /^[A-Za-z0-9._~-]{1,255}$/.test(value);
export function createChatRecoveryJournal(key: string | null, storage: ChatRecoveryStorage | null) {
  const read = (suffix: string, limit: number): ChatRunRecovery[] => {
    try {
      const parsed: unknown =
        key && storage ? JSON.parse(storage.getItem(`${key}:${suffix}`) ?? '[]') : [];
      return Array.isArray(parsed)
        ? parsed
            .filter((item: unknown): item is ChatRunRecovery => {
              if (item === null || typeof item !== 'object') return false;
              const row = item as Record<string, unknown>;
              return (
                ['conversationId', 'cardId', 'quoteId', 'idempotencyKey'].every((field) =>
                  opaque(row[field]),
                ) &&
                Object.keys(row).every((field) =>
                  ['conversationId', 'cardId', 'quoteId', 'idempotencyKey'].includes(field),
                )
              );
            })
            .slice(-limit)
        : [];
    } catch {
      return [];
    }
  };
  let records = read('runs', 100);
  // Bounded identifier-only markers outlive terminal cache writes; a dispatched quote never gets a new key.
  let dispatched = [...read('dispatched-runs', 1000), ...records];
  const save = () => {
    if (!key || !storage) return;
    try {
      storage.setItem(`${key}:dispatched-runs`, JSON.stringify(dispatched));
      storage.setItem(`${key}:runs`, JSON.stringify(records));
    } catch {
      /* Memory-only operation remains possible when browser storage is unavailable. */
    }
  };
  return {
    list: (): readonly ChatRunRecovery[] => records,
    dispatched: (): readonly ChatRunRecovery[] => dispatched,
    find: (cardId: string) =>
      records.find((item) => item.cardId === cardId) ??
      dispatched.find((item) => item.cardId === cardId),
    wasDispatched: (quoteId: string) => dispatched.some((item) => item.quoteId === quoteId),
    put(record: ChatRunRecovery) {
      records = [...records.filter((item) => item.cardId !== record.cardId), record].slice(-100);
      dispatched = [...dispatched.filter((item) => item.quoteId !== record.quoteId), record].slice(
        -1000,
      );
      save();
    },
    remove(cardId: string) {
      records = records.filter((item) => item.cardId !== cardId);
      save();
    },
  };
}

export function withChatRecoveryAction(answer: AnswerMessage): AnswerMessage {
  return {
    ...answer,
    content: {
      ...answer.content,
      drill: [
        ...(answer.content.drill ?? []).filter((chip) => chip.id !== CHAT_RECOVERY_ACTION),
        {
          id: CHAT_RECOVERY_ACTION,
          label: 'بررسی وضعیت پاسخ',
          action: { kind: 'chip', chipId: CHAT_RECOVERY_ACTION, label: 'بررسی وضعیت پاسخ' },
        },
      ],
    },
  };
}

export function uncertainChatAnswer(
  id: string,
  recoverable: boolean,
  checking = false,
): AnswerMessage {
  const answer: AnswerMessage = {
    id,
    role: 'assistant',
    kind: 'answer',
    sampleId: 'server',
    isSample: false,
    content: {
      text: [
        checking
          ? 'در حال بررسی وضعیت همان درخواست؛ اجرای تازه‌ای آغاز نمی‌شود.'
          : recoverable
            ? 'پاسخ نهایی دریافت نشده است. ممکن است درخواست در سرور اجرا شده باشد؛ وضعیت همان درخواست را بررسی کنید.'
            : 'این درخواست پیش از دریافت نتیجه بسته شده است. شناسهٔ بازیابی آن موجود نیست؛ برای وضعیت اجرا و مصرف، گزارش هوش مصنوعی را بررسی کنید.',
      ],
    },
    footer: {
      tokens: 0,
      unit: 'توکن',
      sample: false,
      usageLabel: 'مصرف نامشخص',
      basis: 'نتیجهٔ اجرا هنوز تأیید نشده است',
    },
    context: {
      path: [{ id: `${id}:recovery`, label: 'وضعیت درخواست', kind: 'topic', messageId: id }],
      filters: [],
      sources: [],
    },
  };
  return recoverable && !checking ? withChatRecoveryAction(answer) : answer;
}

/** Rehydration never resumes a paid run or leaves an abandoned progress card blocking input. */
export function restoreInterruptedThreads(
  threads: unknown,
  records: readonly ChatRunRecovery[],
  dispatched: readonly ChatRunRecovery[] = [],
): Readonly<Record<string, readonly ChatMessage[]>> {
  const result: Record<string, readonly ChatMessage[]> = {};
  const source =
    threads !== null && typeof threads === 'object' && !Array.isArray(threads) ? threads : {};
  for (const [conversationId, messages] of Object.entries(source)) {
    if (!Array.isArray(messages)) continue;
    const valid = messages.filter((message: unknown): message is ChatMessage => {
      if (message === null || typeof message !== 'object') return false;
      const row = message as Record<string, unknown>;
      if (!opaque(row.id)) return false;
      const plain = (value: unknown): value is Record<string, unknown> =>
        value !== null && typeof value === 'object' && !Array.isArray(value);
      if (row.role === 'user')
        return (
          row.kind === 'prompt' &&
          typeof row.text === 'string' &&
          Array.isArray(row.scope) &&
          typeof row.period === 'string'
        );
      if (row.role !== 'assistant') return false;
      if (row.kind === 'answer')
        return (
          plain(row.content) &&
          Array.isArray(row.content.text) &&
          plain(row.footer) &&
          typeof row.footer.tokens === 'number' &&
          plain(row.context) &&
          Array.isArray(row.context.path) &&
          Array.isArray(row.context.filters) &&
          Array.isArray(row.context.sources)
        );
      if (row.kind === 'analyzing') return Array.isArray(row.modules);
      if (row.kind === 'stopped') return true;
      if (row.kind === 'denied')
        return row.reason === 'suspended' || row.reason === 'not-permitted';
      if (row.kind === 'clarify')
        return (
          typeof row.question === 'string' &&
          Array.isArray(row.options) &&
          Array.isArray(row.selected) &&
          typeof row.period === 'string'
        );
      if (row.kind === 'error') return plain(row.replay) && typeof row.attempt === 'number';
      if (row.kind === 'confirm-heavy')
        return (
          plain(row.replay) &&
          typeof row.estimate === 'number' &&
          ['pending', 'confirmed', 'cancelled'].includes(String(row.status))
        );
      return (
        row.kind === 'out-of-tokens' &&
        typeof row.estimate === 'number' &&
        typeof row.balance === 'number'
      );
    });
    result[conversationId] = valid.map((message) =>
      message.role === 'assistant' && message.kind === 'analyzing'
        ? message.live
          ? uncertainChatAnswer(
              message.id,
              [...records, ...dispatched].some((record) => record.cardId === message.id),
            )
          : { id: message.id, role: 'assistant' as const, kind: 'stopped' as const }
        : message.role === 'assistant' &&
            message.kind === 'confirm-heavy' &&
            message.quoteId &&
            [...records, ...dispatched].some((record) => record.quoteId === message.quoteId)
          ? { ...message, status: 'confirmed' as const }
          : message,
    );
  }
  for (const record of records) {
    const messages = result[record.conversationId] ?? [];
    const replacement = uncertainChatAnswer(record.cardId, true);
    result[record.conversationId] = messages.some((message) => message.id === record.cardId)
      ? messages.map((message) => (message.id === record.cardId ? replacement : message))
      : [...messages, replacement];
  }
  return result;
}

export function restoreRecoveryConversations(
  raw: unknown,
  records: readonly ChatRunRecovery[],
): readonly ConversationSummary[] {
  const conversations: readonly ConversationSummary[] = Array.isArray(raw)
    ? raw.filter(
        (item: unknown): item is ConversationSummary =>
          item !== null &&
          typeof item === 'object' &&
          opaque((item as ConversationSummary).id) &&
          typeof (item as ConversationSummary).title === 'string',
      )
    : [];
  const missing = [...new Set(records.map((record) => record.conversationId))].filter(
    (id) => !conversations.some((conversation) => conversation.id === id),
  );
  return [
    ...conversations,
    ...missing.map(
      (id): ConversationSummary => ({
        id,
        title: 'درخواست ناتمام',
        pinned: false,
        bucket: 'today',
        tokens: 0,
        isSample: false,
      }),
    ),
  ];
}

/** The only recovery requests are reads using the original key. Never quote or run again. */
export async function readChatRunRecovery(
  provider: Pick<AiProvider, 'getRunByIdempotencyKey' | 'getTurn'>,
  record: ChatRunRecovery,
  signal: AbortSignal,
): Promise<AiTurn | null> {
  const recovery = await provider.getRunByIdempotencyKey(record.idempotencyKey, { signal });
  return recovery.turnId ? provider.getTurn(recovery.turnId, { signal }) : null;
}
